import {
  Braces,
  Download,
  FileJson2,
  Menu,
  Settings,
  Share2,
} from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Composer } from "./components/Composer";
import { EngineSelector } from "./components/EngineSelector";
import { LiveRun, councilSteps, teamSteps } from "./components/RunTimeline";
import { ModelSelector } from "./components/ModelSelector";
import { ModeSelector, MODE_ICON } from "./components/ModeSelector";
import { Sidebar } from "./components/Sidebar";
import { useChat } from "./hooks/useChat";
import { useModels } from "./hooks/useModels";
import { classifyArtifact, normalizeLanguage } from "./lib/artifacts";
import {
  cloneBranchAt,
  encodeSharedConversation,
  importSharedConversation,
} from "./lib/conversations";
import { findMode } from "./lib/modes";
import { archiveToMarkdown, downloadFile } from "./lib/portable";
import { visibleBranch } from "./lib/prompts";
import { useAppStore } from "./lib/store";
import type { AssistantPersona, ChatMessage, PortableArchive } from "./lib/types";

const AgentTeamModal = lazy(() =>
  import("./components/AgentTeamModal").then((m) => ({ default: m.AgentTeamModal })),
);
const AssistantBuilderModal = lazy(() =>
  import("./components/AssistantBuilderModal").then((m) => ({
    default: m.AssistantBuilderModal,
  })),
);
const CouncilModal = lazy(() =>
  import("./components/CouncilModal").then((m) => ({ default: m.CouncilModal })),
);
const EditModal = lazy(() =>
  import("./components/EditModal").then((m) => ({ default: m.EditModal })),
);
const LibraryModal = lazy(() =>
  import("./components/LibraryModal").then((m) => ({ default: m.LibraryModal })),
);
const MessageBubble = lazy(() =>
  import("./components/MessageBubble").then((m) => ({ default: m.MessageBubble })),
);
const RightPanel = lazy(() =>
  import("./components/RightPanel").then((m) => ({ default: m.RightPanel })),
);
const SettingsModal = lazy(() =>
  import("./components/SettingsModal").then((m) => ({ default: m.SettingsModal })),
);

export default function App() {
  const store = useAppStore();
  const {
    models,
    findModel,
    loading: modelsLoading,
    states: modelStates,
    statuses: providerStatuses,
    refresh: refreshModels,
  } = useModels();
  const {
    conversation,
    isStreaming,
    council,
    agentRuns,
    questionSession,
    error,
    send,
    stop,
    regenerate,
    editMessage,
    proceedWithAvailableInformation,
    skipQuestions,
    cancelQuestionSession,
  } = useChat();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [councilOpen, setCouncilOpen] = useState(false);
  const [agentTeamOpen, setAgentTeamOpen] = useState(false);
  const [builderFor, setBuilderFor] = useState<{
    assistant?: AssistantPersona | undefined;
  }>();
  const [libraryTab, setLibraryTab] = useState<"projects" | "assistants">();
  const [editing, setEditing] = useState<ChatMessage>();
  const [notice, setNotice] = useState<string>();
  const bottomRef = useRef<HTMLDivElement>(null);

  const messages = useMemo(
    () =>
      visibleBranch(
        conversation?.messages ?? [],
        conversation?.activeBranchId ?? "",
      ).filter((message) => !message.hidden),
    [conversation?.messages, conversation?.activeBranchId],
  );
  const branchIds = useMemo(
    () => [...new Set((conversation?.messages ?? []).map((message) => message.branchId))],
    [conversation?.messages],
  );
  const currentModel = findModel(store.settings.provider, store.settings.model);
  const assistant = store.assistants.find(
    (item) => item.id === conversation?.assistantId,
  );
  const mode = findMode(conversation?.mode);

  useEffect(() => {
    void useAppStore.getState().hydrate();
  }, []);

  useEffect(() => {
    if (isStreaming) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isStreaming, council, agentRuns]);

  // The theme drives a data attribute rather than a class so that `system`
  // can follow the OS without React re-rendering on every scheme change.
  useEffect(() => {
    const root = document.documentElement;
    const media = window.matchMedia("(prefers-color-scheme: light)");
    const apply = () => {
      const resolved =
        store.settings.theme === "system"
          ? media.matches
            ? "light"
            : "dark"
          : store.settings.theme;
      root.dataset["theme"] = resolved;
    };
    apply();
    if (store.settings.theme !== "system") return;
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [store.settings.theme]);

  useEffect(() => {
    const shared = window.location.hash.startsWith("#share=")
      ? window.location.hash.slice(7)
      : "";
    if (!shared) return;
    try {
      const imported = importSharedConversation(shared);
      store.importConversation(imported.conversation, imported.artifacts);
      window.history.replaceState(null, "", window.location.pathname);
      setNotice("Conversazione condivisa importata");
    } catch {
      setNotice("Link di condivisione non valido");
    }
    window.setTimeout(() => setNotice(undefined), 3000);
    // Runs once on mount: the hash is consumed and cleared immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Promotes an inline code block into a real artifact on demand. */
  const promoteToArtifact = (content: string, language: string) => {
    if (!conversation) return;
    const normalized = normalizeLanguage(language);
    const now = Date.now();
    const id = crypto.randomUUID();
    store.addArtifacts([
      {
        id,
        conversationId: conversation.id,
        title: `Estratto ${normalized}`,
        kind: classifyArtifact(normalized, content),
        language: normalized,
        content,
        revisions: [],
        createdAt: now,
        updatedAt: now,
      },
    ]);
    store.openArtifact(id);
  };

  const branchFrom = (message: ChatMessage) => {
    if (!conversation) return;
    store.updateConversation(conversation.id, cloneBranchAt(conversation, message.id));
  };

  const shareConversation = async () => {
    if (!conversation) return;
    try {
      const payload = encodeSharedConversation(
        conversation,
        store.artifacts.filter((item) => item.conversationId === conversation.id),
      );
      await navigator.clipboard.writeText(
        `${window.location.origin}${window.location.pathname}#share=${payload}`,
      );
      setNotice("Link copiato negli appunti");
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Condivisione non riuscita.");
    }
    window.setTimeout(() => setNotice(undefined), 2600);
  };

  const exportConversation = () => {
    if (!conversation) return;
    const archive: PortableArchive = {
      format: "vesper-ai",
      version: 2,
      exportedAt: new Date().toISOString(),
      conversations: [conversation],
      artifacts: store.artifacts.filter(
        (item) => item.conversationId === conversation.id,
      ),
      projects: [],
      assistants: [],
      settings: store.settings,
    };
    downloadFile(
      `${slug(conversation.title)}.md`,
      archiveToMarkdown(archive),
      "text/markdown",
    );
  };

  const startersFor = assistant?.starters.length ? assistant.starters : mode.starters;

  return (
    <div className="app-shell">
      <Sidebar onOpenLibrary={setLibraryTab} onCreateAssistant={() => setBuilderFor({})} />
      <main className="main-area">
        <header className="topbar">
          <button
            className="icon-button mobile-menu"
            onClick={() => store.setMobileSidebar(true)}
            aria-label="Apri menu"
          >
            <Menu size={18} />
          </button>
          <ModelSelector
            models={models}
            provider={store.settings.provider}
            model={store.settings.model}
            states={modelStates}
            statuses={providerStatuses}
            onRefresh={refreshModels}
            onChange={(provider, model) =>
              store.setSettings({
                provider,
                model,
                modelDefaults: { chat: { provider, model } },
              })
            }
          />
          <EngineSelector
            onConfigureCouncil={() => setCouncilOpen(true)}
            onConfigureAgentTeam={() => setAgentTeamOpen(true)}
          />
          <div className="topbar-center">
            {modelsLoading && <span className="sync-label">sincronizzazione…</span>}
            {!modelsLoading && currentModel && (
              <span className="context-label">
                {formatContext(currentModel.contextWindow)} context
              </span>
            )}
          </div>
          <div className="topbar-actions">
            {branchIds.length > 1 && (
              <select
                className="branch-select"
                aria-label="Branch attivo"
                value={conversation?.activeBranchId}
                onChange={(event) =>
                  conversation &&
                  store.updateConversation(conversation.id, {
                    activeBranchId: event.target.value,
                  })
                }
              >
                {branchIds.map((id, index) => (
                  <option value={id} key={id}>
                    Branch {index + 1}
                  </option>
                ))}
              </select>
            )}
            <button className="top-action" onClick={exportConversation} title="Esporta">
              <Download size={16} />
            </button>
            <button
              className="top-action"
              onClick={() => void shareConversation()}
              title="Condividi"
            >
              <Share2 size={16} />
            </button>
            <button
              className={
                store.settings.rightPanel === "artifact"
                  ? "top-action active"
                  : "top-action"
              }
              onClick={() =>
                store.setSettings({
                  rightPanel:
                    store.settings.rightPanel === "artifact" ? "none" : "artifact",
                })
              }
              title="Artifact"
            >
              <Braces size={16} />
            </button>
            <button
              className={
                store.settings.rightPanel === "raw" ? "top-action active" : "top-action"
              }
              onClick={() =>
                store.setSettings({
                  rightPanel: store.settings.rightPanel === "raw" ? "none" : "raw",
                  rawMode: true,
                })
              }
              title="Raw / API"
            >
              <FileJson2 size={16} />
            </button>
            <button
              className="top-action settings-action"
              onClick={() => setSettingsOpen(true)}
              title="Impostazioni"
            >
              <Settings size={16} />
            </button>
          </div>
        </header>

        <div className="workspace">
          <section className="chat-pane">
            <div className="chat-scroll">
              {!messages.length ? (
                <div className="empty-state">
                  <div className="empty-orb">
                    {assistant ? (
                      <span
                        className="library-avatar hero"
                        style={{ background: assistant.color }}
                      >
                        {assistant.avatar}
                      </span>
                    ) : (
                      <span className="brand-mark hero">
                        <span />
                      </span>
                    )}
                  </div>
                  <p className="eyebrow">
                    {assistant ? assistant.description || "Assistente" : mode.tagline}
                  </p>
                  <h1>{assistant ? assistant.name : greeting(mode.id)}</h1>
                  <p className="empty-lede">{mode.description}</p>
                  <div className="starter-grid">
                    {startersFor.length > 0
                      ? startersFor.map((starter) => (
                          <button key={starter} onClick={() => void send({ content: starter })}>
                            <span className="starter-glyph">{MODE_ICON[mode.id]}</span>
                            <span>{starter}</span>
                          </button>
                        ))
                      : DEFAULT_STARTERS.map((starter) => (
                          <button
                            key={starter.text}
                            onClick={() => void send({ content: starter.text })}
                          >
                            <span className="starter-glyph">{MODE_ICON[starter.mode]}</span>
                            <span>{starter.text}</span>
                          </button>
                        ))}
                  </div>
                </div>
              ) : (
                <div className="message-list">
                  {messages.map((message) => (
                    <Suspense fallback={<div className="message-loading" />} key={message.id}>
                      <MessageBubble
                        message={message}
                        onRegenerate={(item) => void regenerate(item)}
                        onEdit={setEditing}
                        onBranch={branchFrom}
                        onArtifact={promoteToArtifact}
                      />
                    </Suspense>
                  ))}
                  {council.length > 0 && (
                    <LiveRun
                      engine="council"
                      steps={councilSteps(council)}
                      visible={store.settings.council.visible}
                      maxCost={store.settings.council.maxCost}
                    />
                  )}
                  {agentRuns.length > 0 && (
                    <LiveRun
                      engine="agent_team"
                      steps={teamSteps(agentRuns)}
                      visible={store.settings.agentTeam.visible}
                      maxCost={store.settings.agentTeam.maxCost}
                    />
                  )}
                  {error && <div className="error-banner">{error}</div>}
                  <div ref={bottomRef} />
                </div>
              )}
            </div>
            <Composer
              isStreaming={isStreaming}
              models={models}
              questionSession={questionSession}
              onSend={(content, attachments, instruction) =>
                void send({ content, attachments, instruction })
              }
              onStop={stop}
              onProceedQuestions={() => void proceedWithAvailableInformation()}
              onSkipQuestions={() => void skipQuestions()}
              onCancelQuestions={cancelQuestionSession}
              modeSelector={
                <ModeSelector
                  mode={conversation?.mode ?? "standard"}
                  onChange={(next) =>
                    conversation &&
                    store.updateConversation(conversation.id, { mode: next })
                  }
                />
              }
            />
          </section>
          {store.settings.rightPanel !== "none" && (
            <Suspense fallback={null}>
              <RightPanel conversation={conversation} />
            </Suspense>
          )}
        </div>
      </main>

      <Suspense fallback={null}>
        {settingsOpen && (
          <SettingsModal
            onClose={() => setSettingsOpen(false)}
            models={models}
            modelStates={modelStates}
            providerStatuses={providerStatuses}
            onModelsChanged={refreshModels}
          />
        )}
        {councilOpen && (
          <CouncilModal models={models} onClose={() => setCouncilOpen(false)} />
        )}
        {agentTeamOpen && (
          <AgentTeamModal models={models} onClose={() => setAgentTeamOpen(false)} />
        )}
        {builderFor && (
          <AssistantBuilderModal
            existing={builderFor.assistant}
            onClose={() => setBuilderFor(undefined)}
            onCreated={(created) => {
              if (conversation) {
                store.updateConversation(conversation.id, { assistantId: created.id });
              }
              setNotice(`${created.name} è pronto`);
              window.setTimeout(() => setNotice(undefined), 2600);
            }}
          />
        )}
        {libraryTab && (
          <LibraryModal
            initialTab={libraryTab}
            onClose={() => setLibraryTab(undefined)}
            onEditAssistant={(item) => setBuilderFor({ assistant: item })}
          />
        )}
        {editing && (
          <EditModal
            message={editing}
            onClose={() => setEditing(undefined)}
            onSave={(content) => {
              void editMessage(editing, content);
              setEditing(undefined);
            }}
          />
        )}
      </Suspense>
      {notice && (
        <div className="toast">
          <Share2 size={14} /> {notice}
        </div>
      )}
    </div>
  );
}

const DEFAULT_STARTERS = [
  { mode: "learn", text: "Insegnami una cosa nuova che non so di non sapere." },
  { mode: "story", text: "Raccontami una storia breve su un faro che smette di funzionare." },
  { mode: "brainstorm", text: "Aiutami a trovare 20 idee per un progetto che parte lunedì." },
  { mode: "analyze", text: "Trasforma un'idea grezza in un piano concreto e verificabile." },
] as const;

function greeting(mode: string): string {
  switch (mode) {
    case "learn":
      return "Cosa vuoi imparare?";
    case "story":
      return "Cosa raccontiamo?";
    case "brainstorm":
      return "Su cosa ragioniamo?";
    case "critique":
      return "Cosa devo smontare?";
    case "code":
      return "Cosa costruiamo?";
    case "translate":
      return "Cosa traduciamo?";
    case "summarize":
      return "Cosa sintetizziamo?";
    case "debate":
      return "Su cosa discutiamo?";
    case "analyze":
      return "Cosa analizziamo?";
    default:
      return "Cosa vuoi esplorare?";
  }
}

function formatContext(value: number): string {
  if (!value) return "context n/d";
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  return `${Math.round(value / 1000)}K`;
}

function slug(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "conversation"
  );
}
